import { LOGO_URL } from "../lib/constants";

const Section = ({ title, children }) => (
  <section className="mb-8">
    <h2 className="text-lg font-semibold text-slate-900 mb-2">{title}</h2>
    <div className="text-sm leading-relaxed text-slate-600 space-y-2">{children}</div>
  </section>
);

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-slate-50" data-testid="privacy-policy-page">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center gap-3">
          <img src={LOGO_URL} alt="Grian Solar Power" className="h-9 w-auto" data-testid="privacy-logo" />
          <span className="font-semibold text-slate-900">Grian Solar Power</span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12">
        <h1 className="text-3xl font-bold text-slate-900 mb-1">Privacy Policy</h1>
        <p className="text-xs text-slate-400 mb-10">Last updated: June 2026</p>

        <Section title="1. Who we are">
          <p>
            Grian Solar Power (ABN 91 635 362 560) ("we", "us", "our") is an Australian solar energy
            company. This Privacy Policy explains how we collect, use, store and protect your personal
            information when you interact with us, including through our website, our Facebook and
            Instagram Lead Ad forms, and our customer relationship management (CRM) system.
          </p>
        </Section>

        <Section title="2. Information we collect">
          <p>We may collect the following personal information:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Your name, phone number and email address.</li>
            <li>Your property address and roof/site details relevant to a solar installation.</li>
            <li>The solar solution you are interested in (e.g. solar panels, home battery).</li>
            <li>Information you submit through our Facebook/Instagram Lead Ad forms or website enquiry forms.</li>
            <li>Records of our communications with you (calls, SMS, emails and notes).</li>
          </ul>
        </Section>

        <Section title="3. How we collect it">
          <p>
            We collect information directly from you when you submit an enquiry, and via Meta's
            (Facebook/Instagram) Lead Ads platform when you choose to submit one of our lead forms.
            When you submit a Lead Ad form, Meta securely transfers the details you provided to our CRM
            using the Facebook Graph API so our sales team can follow up with you.
          </p>
        </Section>

        <Section title="4. How we use your information">
          <ul className="list-disc pl-5 space-y-1">
            <li>To contact you about your solar enquiry and answer your questions.</li>
            <li>To prepare and provide solar system quotes and rebate estimates.</li>
            <li>To arrange site assessments, installations and related services.</li>
            <li>To keep records of our dealings with you and provide customer support.</li>
          </ul>
          <p>We do not sell your personal information to third parties.</p>
        </Section>

        <Section title="5. Disclosure">
          <p>
            We may share your information with our staff, installers and service providers who help us
            deliver our services, and with government or rebate bodies where required to process
            eligible solar/battery rebates (e.g. STCs and state rebate schemes). We may also disclose
            information where required by law.
          </p>
        </Section>

        <Section title="6. Data storage and security">
          <p>
            Your information is stored in our secure CRM system with access restricted to authorised
            staff. We take reasonable technical and organisational steps to protect your personal
            information from misuse, loss, unauthorised access, modification or disclosure.
          </p>
        </Section>

        <Section title="7. Access, correction and deletion">
          <p>
            You may request access to, correction of, or deletion of the personal information we hold
            about you at any time. To do so, please contact us using the details below, or see our{" "}
            <a href="/data-deletion" className="text-emerald-600 underline" data-testid="privacy-data-deletion-link">
              Data Deletion Instructions
            </a>.
          </p>
        </Section>

        <Section title="8. Contact us">
          <p>
            If you have any questions about this Privacy Policy or how we handle your information,
            contact us at:
          </p>
          <p className="font-medium text-slate-800">
            Grian Solar Power<br />
            Website: <a href="https://griansolarpower.com.au" className="text-emerald-600 underline">griansolarpower.com.au</a><br />
            Email: <a href="mailto:info@griansolarpower.com.au" className="text-emerald-600 underline">info@griansolarpower.com.au</a><br />
            Phone: <a href="tel:0485976518" className="text-emerald-600 underline">0485 976 518</a>
          </p>
        </Section>
      </main>

      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-3xl mx-auto px-6 py-6 text-xs text-slate-400">
          © {new Date().getFullYear()} Grian Solar Power. ABN 91 635 362 560.
        </div>
      </footer>
    </div>
  );
}
